import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { FaGift, FaCopy, FaCheck, FaTimes, FaUsers, FaArrowRight, FaShareAlt } from "react-icons/fa";
import { toast } from "./ui/Toast";
import { usePortalContent, getReferAndEarnPageMeta } from "../portal";

const ReferralRewardModal = ({ isOpen, onClose }) => {
  const { content: portalContent } = usePortalContent('refer-and-earn');
  const meta = useMemo(() => getReferAndEarnPageMeta(portalContent), [portalContent]);
  const reward = meta.rewardModal;
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchReferralData();
    }
  }, [isOpen]);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (referralData?.referralLink) {
      navigator.clipboard.writeText(referralData.referralLink);
      setCopied(true);
      toast.success(meta.toasts.linkCopied);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (referralData?.referralLink) {
      try {
        if (navigator.share) {
          await navigator.share({
            title: 'Sign up for BooksComply',
            text: 'Use my referral link to sign up for BooksComply and get rewarded!',
            url: referralData.referralLink,
          });
          toast.success(reward.shareSuccess);
        } else {
          handleCopyLink();
          toast.info(meta.toasts.linkCopied);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          toast.error(reward.shareError);
        }
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-fade-in">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-[480px] max-h-[calc(100vh-2rem)] overflow-y-auto animate-scale-in border border-gray-100 dark:border-dark-700 custom-scrollbar">

        <div className="p-6 text-center relative border-b border-gray-50 dark:border-dark-700/50">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <FaTimes className="w-4 h-4" />
          </button>

          <div
            className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg relative group"
            style={{
              background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
              boxShadow: `0 10px 15px -3px var(--glow-color)`
            }}
          >
            <FaGift className="w-8 h-8 text-white relative z-10" />
            <div className="absolute inset-0 bg-white/20 rounded-2xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          <h2 className="section-title tracking-tight">{reward.title}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {reward.subtitlePrefix}{' '}
            <span className="text-primary-600 dark:text-primary-500 font-bold">{reward.subtitleHighlight}</span>
          </p>
        </div>

        <div className="p-6 space-y-6">

          <div className="p-4 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100/30 dark:from-primary-900/10 dark:to-primary-900/20 border border-primary-100/50 dark:border-primary-800/30">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white dark:bg-dark-700 shadow-sm flex items-center justify-center flex-shrink-0 border border-primary-100 dark:border-primary-800">
                <FaUsers className="w-5 h-5 text-primary-500" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-dark-800 dark:text-white text-sm">{reward.cardTitle}</h3>
                  {reward.cardBadge && (
                    <span className="px-1.5 py-0.5 rounded-md bg-red-500 text-white text-xs font-bold uppercase tracking-widest">{reward.cardBadge}</span>
                  )}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {reward.cardBody}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                {reward.linkLabel}
              </label>
              <span className="text-xs font-bold text-primary-500 uppercase tracking-widest">{reward.shareHint}</span>
            </div>
            <div className="flex items-center gap-2 p-1.5 pl-4 rounded-xl bg-gray-50 dark:bg-dark-900/50 border border-gray-200 dark:border-dark-700 focus-within:border-primary-500/50 transition-all duration-300">
              <span className="flex-1 text-xs text-gray-500 dark:text-gray-400 truncate font-mono font-medium py-1.5">
                {referralData?.referralLink || (loading ? reward.linkLoading : reward.linkError)}
              </span>
              <button
                onClick={handleCopyLink}
                disabled={!referralData?.referralLink}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-bold transition-all duration-200 ${copied
                    ? 'bg-green-500 text-white shadow-md'
                    : 'bg-primary-500 text-white hover:bg-primary-600 active:scale-95'
                  } disabled:opacity-50`}
                title="Copy Link"
              >
                {copied ? <FaCheck className="w-3 h-3" /> : <FaCopy className="w-3 h-3" />}
                <span className="text-xs uppercase">{copied ? reward.copiedLabel : reward.copyLabel}</span>
              </button>

              <button
                onClick={handleShare}
                disabled={!referralData?.referralLink}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-bold bg-secondary-500 text-white hover:bg-secondary-600 active:scale-95 transition-all duration-200 disabled:opacity-50"
                title="Share Link"
              >
                <FaShareAlt className="w-3 h-3" />
                <span className="text-xs uppercase">{reward.shareLabel}</span>
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={onClose}
              className="w-full py-3.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm hover:scale-[1.01] active:scale-[0.99]"
              style={{
                background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
                boxShadow: `0 8px 12px -3px var(--glow-color)`
              }}
            >
              {reward.ctaButton} <FaArrowRight className="w-3 h-3" />
            </button>
            <button
              onClick={onClose}
              className="w-full py-2 rounded-lg font-bold text-gray-400 dark:text-gray-500 hover:text-primary-500 dark:hover:text-primary-400 transition-all duration-200 text-xs tracking-wide"
            >
              {reward.skipLabel}
            </button>
          </div>

          <div className="pt-4 border-t border-gray-100 dark:border-dark-700/50 text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500 uppercase font-bold tracking-[0.15em]">
              {reward.footerNote}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReferralRewardModal;
